import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { fetchShowcase, type ShowcaseLinks, type ShowcaseVideo } from '../api/client';
import { Portrait } from './Portrait';
import { SocialIcon } from './SocialIcon';

/**
 * トップページのいちばん下。管理者が選んだ相性ドラマの動画（3 本まで）と、SNS のリンク（doc/VIDEO.md §9）。
 *
 * どちらも自動では始めない（押すまで動画を読み込まない）。表紙は 2 人の絵と再生ボタン。
 * - パソコン（幅 720px 以上）: 3 本を横に並べ、押したその場で読み込んで音ありで再生する。別の動画を始めたら前のは止める。
 * - スマホ: 押すと全画面の再生画面を開き、音ありで再生する。左右にスワイプすると次の動画に移って、そちらを再生する。
 * 動画もリンクも無ければ、何も出さない。
 */
const WIDE = '(min-width: 720px)';

export function TopShowcase() {
  const [videos, setVideos] = useState<readonly ShowcaseVideo[]>([]);
  const [links, setLinks] = useState<ShowcaseLinks | null>(null);
  const [playing, setPlaying] = useState<string | null>(null); // パソコン: その場で再生している動画
  const [viewer, setViewer] = useState<number | null>(null); // スマホ: 全画面で開いている動画の番号

  useEffect(() => {
    let cancelled = false;
    void fetchShowcase()
      .then((r) => { if (!cancelled) { setVideos(r.videos); setLinks(r.links); } })
      .catch(() => undefined); // 出せなくてもトップページは困らない
    return () => { cancelled = true; };
  }, []);

  const open = (index: number) => {
    if (window.matchMedia(WIDE).matches) setPlaying(videos[index].videoId);
    else setViewer(index);
  };

  const socials = links
    ? ([['x', 'X'], ['instagram', 'Instagram'], ['youtube', 'YouTube'], ['tiktok', 'TikTok']] as const).filter(([key]) => links[key])
    : [];
  if (videos.length === 0 && socials.length === 0) return null;

  return (
    <section className="top__section showcase" aria-labelledby="showcase-heading">
      {videos.length > 0 && (
        <>
          <div className="top__section-head">
            <h2 className="top__heading" id="showcase-heading">キャラ同士の相性ドラマ</h2>
            <p className="top__section-note">診断のキャラクター 2 人の性格の違いから作った、短いドラマ動画です。診断の入口から、見たい 2 人と場面をリクエストすることもできます。</p>
          </div>
          <ul className="showcase__rail" aria-label="相性ドラマの動画">
            {videos.map((v, index) => (
              <li key={v.videoId} className="showcase__card">
                {playing === v.videoId
                  ? <InlinePlayer video={v} onEnded={() => setPlaying(null)} />
                  : <Cover video={v} onPlay={() => open(index)} />}
                <p className="showcase__title">{v.title}</p>
                <p className="showcase__meta">
                  <Link to={`/t/${encodeURIComponent(v.themeId)}`}>{v.themeTitle}</Link>
                  {' '}— {v.characters.map((c) => c.name).join(' × ')}
                </p>
              </li>
            ))}
          </ul>
          {videos.length > 1 && <p className="showcase__hint">押すと再生します。スマホでは全画面になり、左右にスワイプで次の動画へ。</p>}
        </>
      )}
      {socials.length > 0 && (
        <div className="showcase__social">
          <p className="top__section-note">GenDan の SNS</p>
          <ul className="showcase__social-list">
            {socials.map(([key, label]) => (
              <li key={key}>
                <a
                  className={`button button--ghost button--icon showcase__social-link showcase__social-link--${key}`}
                  href={links![key]!} target="_blank" rel="noopener noreferrer" aria-label={`GenDan の ${label}`} title={label}
                >
                  <SocialIcon service={key} size={22} />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {viewer !== null && <FullscreenViewer videos={videos} start={viewer} onClose={() => setViewer(null)} />}
    </section>
  );
}

/** 表紙（まだ読み込まない）。2 人の絵と再生ボタン。 */
function Cover({ video, onPlay }: { readonly video: ShowcaseVideo; readonly onPlay: () => void }) {
  return (
    <button type="button" className={`showcase__cover${video.aspectRatio === '16:9' ? ' showcase__cover--wide' : ''}`} onClick={onPlay} aria-label={`${video.title}を再生する`}>
      <span className="showcase__faces" aria-hidden="true">
        {video.characters.slice(0, 2).map((c) => (
          <Portrait key={c.name} brief={{ name: c.name, portrait: c.portrait, emoji: c.emoji }} size={96} className="showcase__face" loading="lazy" />
        ))}
      </span>
      <span className="showcase__names" aria-hidden="true">{video.characters.map((c) => c.name).join(' × ')}</span>
      <span className="showcase__play" aria-hidden="true">▶</span>
    </button>
  );
}

/** 読み込み中の輪。`playing` が来たら消す。 */
function useLoading(ref: RefObject<HTMLVideoElement | null>, active: boolean) {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const v = ref.current;
    if (!v || !active) return undefined;
    const on = () => setLoading(true);
    const off = () => setLoading(false);
    v.addEventListener('waiting', on);
    v.addEventListener('playing', off);
    v.addEventListener('pause', off);
    v.addEventListener('error', off);
    return () => {
      v.removeEventListener('waiting', on);
      v.removeEventListener('playing', off);
      v.removeEventListener('pause', off);
      v.removeEventListener('error', off);
    };
  }, [ref, active]);
  return [loading, setLoading] as const;
}

/** パソコン: 押したその場で読み込み、音ありで再生する。 */
function InlinePlayer({ video, onEnded }: { readonly video: ShowcaseVideo; readonly onEnded: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [loading] = useLoading(ref, true);
  useEffect(() => {
    // 押した操作の続きなので、音ありでも再生できる
    void ref.current?.play().catch(() => undefined);
  }, []);
  return (
    <div className="showcase__player">
      <video
        ref={ref}
        className={`showcase__video${video.aspectRatio === '16:9' ? ' showcase__video--wide' : ''}`}
        src={video.url} controls playsInline preload="auto" onEnded={onEnded} aria-label={video.title}
      />
      {loading && <span className="showcase__spinner" role="status" aria-label="読み込んでいます" />}
    </div>
  );
}

/**
 * スマホ: 全画面の再生画面。左右にスワイプで次の動画（scroll-snap）。いま見えている 1 本だけを音ありで再生し、ほかは止める。
 * スワイプのあとの再生をブラウザが止めたら（音ありの自動再生の制限）、真ん中に再生ボタンを出して押してもらう。
 */
function FullscreenViewer({ videos, start, onClose }: { readonly videos: readonly ShowcaseVideo[]; readonly start: number; readonly onClose: () => void }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [active, setActive] = useState(start);
  // 動画の出来事（playing・waiting）は描き直しより先に来ることがあるので、いまの番号は ref でも持つ
  const activeRef = useRef(start);
  // 読み込む動画（いまの 1 本と両隣）。一度読み込んだものは外さない（外すと戻ったときに読み込み直しになる）
  const [loaded, setLoaded] = useState<ReadonlySet<number>>(() => new Set([start - 1, start, start + 1]));
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  // 親の描き直しで onClose が作り直されても、開いたときの処理をやり直さない
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const playAt = useCallback((index: number) => {
    videoRefs.current.forEach((v, i) => { if (v && i !== index) { v.pause(); } });
    const v = videoRefs.current[index];
    if (!v) return;
    activeRef.current = index;
    v.muted = false;
    setLoading(true);
    v.play()
      .then(() => { if (activeRef.current === index) { setBlocked(false); if (!v.paused) setLoading(false); } })
      .catch(() => { if (activeRef.current === index) { setBlocked(true); setLoading(false); } });
  }, []);

  // 開いたら: 押した動画の位置へ飛び、再生する。ページのスクロールを止め、できる端末では本当の全画面にする
  useEffect(() => {
    const rail = railRef.current;
    if (rail) rail.scrollLeft = start * rail.clientWidth;
    playAt(start);
    const body = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    void rootRef.current?.requestFullscreen?.().catch(() => undefined);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = body;
      window.removeEventListener('keydown', onKey);
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    };
  }, [start, playAt]);

  // 全画面を端末の操作（戻る・下スワイプ）で抜けたら、再生画面も閉じる
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) closeRef.current(); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // スワイプで止まった位置の動画を再生する
  const onScroll = () => {
    const rail = railRef.current;
    if (!rail) return;
    const index = Math.round(rail.scrollLeft / rail.clientWidth);
    if (index !== activeRef.current && index >= 0 && index < videos.length) {
      setActive(index);
      setLoaded((cur) => new Set([...cur, index - 1, index, index + 1]));
      playAt(index);
    }
  };

  const current = videos[active];
  return (
    <div className="viewer" ref={rootRef} role="dialog" aria-modal="true" aria-label="相性ドラマの動画">
      <div className="viewer__rail" ref={railRef} onScroll={onScroll}>
        {videos.map((v, index) => (
          <div key={v.videoId} className="viewer__slide">
            <video
              ref={(el) => { videoRefs.current[index] = el; }}
              className="viewer__video"
              src={loaded.has(index) ? v.url : undefined}
              playsInline preload={index === active ? 'auto' : 'metadata'} loop controls={index === active}
              onPlaying={() => { if (index === activeRef.current) setLoading(false); }}
              onPause={() => { if (index === activeRef.current) setLoading(false); }}
              onWaiting={() => { if (index === activeRef.current) setLoading(true); }}
              aria-label={v.title}
            />
          </div>
        ))}
      </div>
      {loading && !blocked && <span className="showcase__spinner viewer__spinner" role="status" aria-label="読み込んでいます" />}
      {blocked && (
        <button type="button" className="viewer__tap" onClick={() => playAt(active)} aria-label="再生する">▶</button>
      )}
      <div className="viewer__bar">
        <span className="viewer__title">{current.title}</span>
        <span className="viewer__count">{active + 1} / {videos.length}</span>
        <button type="button" className="viewer__close" onClick={onClose} aria-label="閉じる">✕</button>
      </div>
      {videos.length > 1 && <p className="viewer__hint">左右にスワイプで次の動画</p>}
    </div>
  );
}
